import React, { useMemo } from 'react';
import { Text, Box, Skeleton } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import { LineChart } from '@mantine/charts';
import type { CostTimeSeriesViewModel } from '@models/cost';
import './CostChart.scss';

interface CostChartProps {
    data: CostTimeSeriesViewModel | null;
    isLoading: boolean;
}

const CostChart: React.FC<CostChartProps> = ({ data, isLoading }) => {
    const chartData = useMemo(() => {
        if (!data) return [];
        return data.dataPoints.map((point) => ({
            timestamp: new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                hour: data.granularity === 'hour' ? 'numeric' : undefined,
            }).format(point.timestamp),
            Cost: parseFloat(point.costAmount),
        }));
    }, [data]);

    const formatCurrency = (value: number) => {
        if (!data) return value.toString();

        const absValue = Math.abs(value);
        const maximumFractionDigits =
            absValue >= 100 ? 0 :
                absValue >= 1 ? 2 :
                    absValue > 0 ? 4 :
                        2;

        const minimumFractionDigits = absValue > 0 && absValue < 1 ? 2 : 0;

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: data.currency,
            minimumFractionDigits,
            maximumFractionDigits,
        }).format(value);
    };

    if (isLoading) {
        return (
            <Card>
                <Skeleton height={32} width="40%" mb="sm" />
                <Skeleton height={300} mt="sm" />
            </Card>
        );
    }

    if (!data || chartData.length === 0) {
        return (
            <Card>
                <Text fw={600} size="lg" mb={4}>Cost Trend</Text>
                <Text>No data available. Select a date range to view cost trends.</Text>
            </Card>
        );
    }

    return (
        <Card className="cost-chart-card" withBorder>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <Box>
                    <Text fw={600} size="lg" mb={4}>Cost Trend</Text>
                    <Text size="sm" c="dimmed">
                        {data.granularity.charAt(0).toUpperCase() + data.granularity.slice(1)} breakdown
                    </Text>
                </Box>
                <Box className="total-indicator">
                    <Text size="sm" c="dimmed">Total</Text>
                    <Text fw={600} size="lg" c="brand">{formatCurrency(parseFloat(data.totalCost))}</Text>
                </Box>
            </Box>
            <LineChart
                h={300}
                data={chartData}
                dataKey="timestamp"
                series={[{ name: 'Cost', color: 'brand.6' }]}
                curveType="monotone"
                valueFormatter={formatCurrency}
                withLegend
            />
        </Card>
    );
};

export default CostChart;
